import { Divider } from '@hover/blueprint';
import { useCompositorControls } from '@hoverinc/rendering-compositor-react';
import React, { useContext } from 'react';

import { TranslationNamespace } from 'src/i18n/types';
import { FilteredBySubHeader } from 'src/modules/designStudio/components/DesignMenu/FilteredBySubHeader';
import { InteractiveRenderingRegionsMenu } from 'src/modules/designStudio/components/DesignMenu/Menu/InteractiveRenderingRegionsMenu';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { FeatureCategories } from 'src/modules/designStudio/constants';
import { getRenderSelectorsForFilters } from 'src/modules/designStudio/helpers/filters';
import {
  InteractiveRenderingStateContext,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import { emptyIRRegionSettings } from 'src/modules/designStudio/providers/InteractiveRenderingProvider/InteractiveRenderingState';
import { useTranslate } from 'src/shared/hooks';

import { ColorList, TextureList, TypeFilter } from '../Filters';

export const InteractiveRenderingRoofMenu = ({
  requestingOrgName,
  closeMenu,
  onBack,
}: {
  requestingOrgName?: string;
  closeMenu: () => void;
  onBack?: () => void;
}) => {
  const { selectedRegion } = useCompositorControls();
  const { updateDesign, regionSettings, designConfig } = useContext(
    InteractiveRenderingStateContext,
  );
  const { menuItems, filteredColors, filterTracker } = useContext(
    productCatalogContext,
  );
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  const { color, textureId } =
    regionSettings?.[selectedRegion || ''] ?? emptyIRRegionSettings;

  const selectedMenuItem = menuItems.find(item => item.textureId === textureId);

  const subHeader =
    requestingOrgName && filterTracker[FeatureCategories.ROOF] ? (
      <FilteredBySubHeader companyName={requestingOrgName} />
    ) : undefined;

  const { shouldRenderColorList, shouldRenderTextureList } =
    getRenderSelectorsForFilters(selectedMenuItem);

  return (
    <>
      <MenuHeader
        closeMenu={closeMenu}
        disableDisclaimer
        headerText={t('menu.roof.title')}
        onBack={onBack}
      />
      <TypeFilter
        designConfigCategory={FeatureCategories.ROOF}
        previousColor={color}
        selectedMenuItem={selectedMenuItem}
        subHeader={subHeader}
        updateDesign={updateDesign}
      />
      <Divider orientation="horizontal" />
      {shouldRenderColorList ? (
        <>
          <ColorList
            color={color}
            colorData={filteredColors[FeatureCategories.ROOF]}
            designConfig={designConfig}
            designConfigCategory={FeatureCategories.ROOF}
            updateDesign={updateDesign}
          />
          <Divider orientation="horizontal" />
        </>
      ) : null}
      {shouldRenderTextureList ? (
        <>
          <TextureList
            designConfigCategory={FeatureCategories.ROOF}
            selectedMenuItem={selectedMenuItem}
            updateDesign={updateDesign}
          />
          <Divider orientation="horizontal" />
        </>
      ) : null}
    </>
  );
};

export const InteractiveRenderingRoofRegionsMenu = ({
  closeMenu,
}: {
  closeMenu: () => void;
}) => (
  <InteractiveRenderingRegionsMenu
    category="ROOFS"
    categoryMenu={InteractiveRenderingRoofMenu}
    closeMenu={closeMenu}
  />
);
